import type { Message } from '@/types/api';

/** Idempotent toggle of one user's reaction on a message's reaction list —
 *  used for both the optimistic update and the (safe to re-apply) socket echo.
 *
 *  `delta = 1` adds the user to the emoji's group (creating it if absent);
 *  `delta = -1` removes them. Counts are recomputed from the user lists and
 *  empty groups are dropped, so re-applying the same delta is a no-op. The
 *  input is never mutated. */
export function applyReaction(
  reactions: Message['reactions'],
  userId: string,
  emoji: string,
  delta: 1 | -1,
): Message['reactions'] {
  const groups = reactions.map((r) => ({ ...r, users: [...r.users] }));
  const g = groups.find((r) => r.emoji === emoji);
  if (delta === 1) {
    if (g) {
      if (!g.users.includes(userId)) g.users.push(userId);
    } else {
      groups.push({ emoji, count: 0, users: [userId] });
    }
  } else if (g) {
    g.users = g.users.filter((u) => u !== userId);
  }
  return groups
    .map((r) => ({ ...r, count: r.users.length }))
    .filter((r) => r.count > 0);
}
